import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { ActivatedRouteSnapshot, RouterStateSnapshot } from '@angular/router';
import { forkJoin, Observable, of } from 'rxjs';
import { catchError, map, timeout } from 'rxjs/operators';
import { InitialData } from 'app/app.types';
import { NotificationsService } from 'app/layout/common/notifications/notifications.service';
import { SessionNavigationService } from 'app/shared/navigation/session-navigation.service';
import { isExternalStoreHost, isServicesBoHost } from 'app/modules/services-store/services-host.util';

@Injectable({
    providedIn: 'root'
})
export class InitialDataResolver {
    private readonly _resolverTimeoutMs = 12000;

    constructor(
        private _httpClient: HttpClient,
        private _sessionNavigation: SessionNavigationService,
        private _notifications: NotificationsService
    ) {
    }

    resolve(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<InitialData> {
        const messages$ = (isServicesBoHost() || isExternalStoreHost()) ? of([]) : this._safeGet('api/common/messages', []);

        return forkJoin([
            messages$,
            this._safeGet('api/common/navigation', {}),
            this._safeNotifications(),
            this._safeGet('api/common/shortcuts', [])
        ]).pipe(
            map(([messages, _navigation, notifications, shortcuts]) => {
                const initial: InitialData = {
                    messages,
                    navigation: {
                        compact: [],
                        default: [],
                        futuristic: [],
                        horizontal: [],
                    },
                    notifications,
                    shortcuts,
                    user: null as unknown as InitialData['user'],
                };

                this._sessionNavigation.setBaseInitialData(initial);

                return this._sessionNavigation.getInitialData();
            }),
            catchError((error) => {
                console.error('[InitialDataResolver] Failed to resolve initial data after sign-in.', {
                    route: state.url,
                    error
                });

                return of({
                    messages: [],
                    navigation: {
                        compact: [],
                        default: [],
                        futuristic: [],
                        horizontal: []
                    },
                    notifications: [],
                    shortcuts: [],
                    user: null
                } as unknown as InitialData);
            })
        );
    }

    /** Notification Hub inbox for the current app + user (scoped by X-APP-CODE + bearer). */
    private _safeNotifications(): Observable<InitialData['notifications']> {
        return this._notifications.getAll().pipe(
            timeout(this._resolverTimeoutMs),
            catchError((error) => {
                console.error('[InitialDataResolver] Notification Hub inbox failed, using fallback.', { error });
                return of([] as InitialData['notifications']);
            })
        );
    }

    private _safeGet<T>(url: string, fallback: T): Observable<T> {
        return this._httpClient.get<T>(url).pipe(
            timeout(this._resolverTimeoutMs),
            catchError((error) => {
                console.error('[InitialDataResolver] GET failed, using fallback.', {
                    url,
                    error
                });
                return of(fallback);
            })
        );
    }
}
